import { createContext, ReactNode, useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { IUXJourney, IUXSchema, IUXTool } from "../journeys/types/journeyUxSchema.types";
import { runJourneyPlanner as runPlanner } from "../journeys/services/journeyService";
import { usePortal } from "./PortalContext";
import { useUserAuth } from "./AuthContext";

export interface IJourneyContextType {
  uxSchema: IUXSchema | null;
  currentJourney: IUXJourney | null;
  nextBestJourney: IUXJourney | null;
  currentTool: IUXTool | null;
  isEvaluating: boolean;
  navigateToNextTool: () => void;
}

const JourneyContext = createContext<IJourneyContextType | undefined>(undefined);

export function JourneyContextProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const { clientData, isLoading } = usePortal();
  const { user, loading: authLoading } = useUserAuth();
  
  const [uxSchema, setUxSchema] = useState<IUXSchema | null>(null);
  const [isEvaluating, setIsEvaluating] = useState(false);

  const product = uxSchema?.products[0] ?? null;
  const currentJourney: IUXJourney | null = product?.primaryJourney ?? null;

  const currentTool: IUXTool | null =
    currentJourney?.tools.find((t) => t.status === "current") ?? null;

  useEffect(() => {
    if (isLoading || authLoading) return;
    const auth = { isAuthenticated: !!user, hasClientData: !!clientData };

    setIsEvaluating(true);
    runPlanner(clientData ?? null, auth)
      .then((schema) => {
        setUxSchema(schema);
      })
      .finally(() => setIsEvaluating(false));
  }, [clientData, user, isLoading, authLoading]);

  function navigateToNextTool(): void {
    if (currentTool?.route) {
      navigate(currentTool.route);
    }
  }

  const nextBestJourney: IUXJourney | null = product?.nextBestJourney ?? null;

  return (
    <JourneyContext.Provider
      value={{
        uxSchema,
        currentJourney,
        nextBestJourney,
        currentTool,
        isEvaluating,
        navigateToNextTool,
      }}
    >
      {children}
    </JourneyContext.Provider>
  );
}

export function useJourneyContext(): IJourneyContextType {
  const context = useContext(JourneyContext);
  if (!context) {
    throw new Error("useJourney must be used within a JourneyContextProvider");
  }
  return context;
}
